import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAppContext } from '../../../Store/Store';
import StarRating from '../../Extra/Stars/StarRating';

interface StarsScreenProps {
  onNext: () => void;
  feedbackData: any;
  setFeedbackData: (data: any) => void;
  goBack: () => void;
  showSkipNextScreenButton?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
}

export default function StarsScreen({
  onNext,
  feedbackData,
  setFeedbackData,
  goBack,
  showSkipNextScreenButton = false,
  showBackButton = false,
  onBack,
}: StarsScreenProps) {
  const { backendBaseUrl, product, style, event, userEmail } = useAppContext();

  const [rating, setRating] = useState<number>(0);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [progressIntervalId, setProgressIntervalId] =
    useState<NodeJS.Timeout | null>(null);
  const [progressWidth, setProgressWidth] = useState<number>(100);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  console.log('⭐ StarsScreen mounted/updated:', {
    feedbackData: feedbackData ? 'has data' : 'no data',
    feedbackDataContent: feedbackData,
    product_code: product?.code,
    rating,
    isUpdating,
  });

  const updateFeedbackRating = async (newRating: number) => {
    console.log('📊 updateFeedbackRating called:', {
      newRating,
      feedbackData: feedbackData ? 'has data' : 'no data',
      feedbackId: feedbackData?.feedbackId,
      product_code: product?.code,
      userEmail,
    });

    if (!feedbackData?.feedbackId) {
      console.log('❌ No feedbackId found, cannot update rating');
      return;
    }

    setIsUpdating(true);
    try {
      const response = await axios.patch(
        `${backendBaseUrl}/update-feedback-rating/`,
        {
          feedback_id: feedbackData.feedbackId,
          rating: newRating,
        }
      );
      console.log('✅ Rating updated successfully:', {
        newRating,
        response: response.data,
      });
    } catch (error) {
      console.error('❌ Failed to update rating:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Start 5-second timeout and progress bar when component mounts
  useEffect(() => {
    console.log('🚀 StarsScreen useEffect starting timeout and progress');

    // Main timeout for auto-rating
    const timeout = setTimeout(() => {
      console.log(
        '⏰ 5-second timeout reached, setting rating to 0 and proceeding'
      );
      updateFeedbackRating(0);
      onNext();
    }, 5000);

    setTimeoutId(timeout);

    // Progress bar animation - update every 50ms for smooth animation
    const startTime = Date.now();
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 5000 - elapsed);
      const newWidth = (remaining / 5000) * 100;

      setProgressWidth(newWidth);

      if (remaining <= 0) {
        clearInterval(progressInterval);
      }
    }, 50);

    setProgressIntervalId(progressInterval);

    return () => {
      console.log('🧹 StarsScreen cleanup, clearing timeouts');
      if (timeout) {
        clearTimeout(timeout);
      }
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, []);

  const handleRating = (value: number) => {
    console.log('⭐ handleRating called:', {
      value,
      timeoutId: timeoutId ? 'exists' : 'null',
      progressIntervalId: progressIntervalId ? 'exists' : 'null',
    });

    if (timeoutId) {
      console.log('⏰ Clearing timeout');
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }

    if (progressIntervalId) {
      console.log('📊 Clearing progress interval');
      clearInterval(progressIntervalId);
      setProgressIntervalId(null);
    }

    setRating(value);
    updateFeedbackRating(value);

    setTimeout(() => {
      console.log('🚀 Proceeding to next screen after rating');
      onNext();
    }, 300);
  };

  const handleDeleteFeedback = () => {
    console.log('🗑️ handleDeleteFeedback called');

    // Clear timeout and progress if active
    if (timeoutId) {
      console.log('⏰ Clearing timeout for delete');
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }

    if (progressIntervalId) {
      console.log('📊 Clearing progress interval for delete');
      clearInterval(progressIntervalId);
      setProgressIntervalId(null);
    }

    setFeedbackData(null);
    goBack();
  };

  const handleSkip = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    if (progressIntervalId) {
      clearInterval(progressIntervalId);
      setProgressIntervalId(null);
    }
    updateFeedbackRating(0);
    onNext();
  };

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 'clamp(12px, 2vh, 24px)',
        padding: 'clamp(12px, 2vh, 24px)',
        boxSizing: 'border-box',
        margin: 'auto',
      }}
    >
      <StarRating rating={rating} setRating={handleRating} />
      <div style={{ fontWeight: 'bold', fontSize: '100%' }}>
        Evaluate your experience
      </div>

      {/* Countdown Progress Bar */}
      <div
        className="yf-field"
        style={{
          height: '4px',
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          borderRadius: '2px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${progressWidth}%`,
            height: '100%',
            backgroundColor:
              progressWidth > 50
                ? '#4CAF50'
                : progressWidth > 20
                  ? '#FF9800'
                  : '#F44336',
            borderRadius: '2px',
            transition: 'width 0.05s linear',
          }}
        />
      </div>

      {event?.show_delete_feedback_option && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 'clamp(16px, 4vh, 48px)',
            cursor: 'pointer',
            color: style?.delete_feedback_button_text_color || '#000',
            backgroundColor:
              style?.delete_feedback_button_bg_color || 'transparent',
            boxShadow: style?.delete_feedback_button_shadow_color
              ? `2px 2px 4px ${style.delete_feedback_button_shadow_color}`
              : 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            border: '1px solid #ccc',
          }}
          onClick={handleDeleteFeedback}
        >
          {style?.delete_feedback_button_indication || 'Delete Feedback'}
        </div>
      )}
      {showBackButton && (
        <button
          type="button"
          onClick={() => onBack?.()}
          style={{
            fontSize: '18px',
            textDecoration: 'underline',
            color: style?.back_button_text_color || '#07296A',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            marginTop: 'clamp(16px, 4vh, 48px)',
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: style?.back_button_indication || 'Back',
            }}
          />
        </button>
      )}
      {showSkipNextScreenButton && (
        <button
          type="button"
          onClick={handleSkip}
          style={{
            fontSize: '18px',
            textDecoration: 'underline',
            color: style?.skip_next_screen_button_text_color || '#07296A',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            marginTop: 'clamp(16px, 4vh, 48px)',
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: style?.skip_next_screen_button_indication || 'Skip',
            }}
          />
        </button>
      )}
    </div>
  );
}
